import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import backend from '../services/backend';
import clsx from 'clsx';
import BackButton from '../components/BackButton';
import { PlusIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import Tag from '../components/Tag';
import FileSize from '../components/FileSize';
import { Input } from '../catalyst/input';
import { Textarea } from '../catalyst/textarea';
import { usePlatformData } from '../hooks/usePlatformData';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import AuthGuard from '../components/AuthGuard';
import { Button } from '../catalyst/button';
import { uploadFileInChunks } from '../lib/uploadFileInChunks';
import pLimit from 'p-limit';

export default function Upload() {
  useDocumentTitle('Upload');
  const platformData = usePlatformData();
  const [files, setFiles] = useState<File[]>([]);
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkUrlsText, setLinkUrlsText] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [fileProgress, setFileProgress] = useState<Array<number>>();
  const navigate = useNavigate();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFiles(
        Array.from(event.target.files).filter((file) => {
          return /\.(pdf|zip|jpg|jpeg|png|xlsx|xls|csv|html|htm|doc|docx)$/i.test(file.name);
        }),
      );
      setFileProgress(undefined);
    }
  };

  const parseLinkUrls = (text: string): string[] =>
    text
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);

  const validateLinkUrl = (url: string): string | null => {
    if (!url) return null;

    try {
      const urlObj = new URL(url);

      if (urlObj.protocol === 'gcs:' || urlObj.protocol === 'gs:') {
        return null;
      }

      const host = urlObj.hostname.toLowerCase();
      if (host !== 'dropbox.com' && !host.endsWith('.dropbox.com')) {
        return 'URL must be from dropbox.com or a gs:// / gcs:// link';
      }
      // Check if it's a shared link
      if (!urlObj.pathname.includes('/s/')) {
        return 'URL must be a Dropbox shared link';
      }
      return null;
    } catch {
      return 'Invalid URL format';
    }
  };

  const handleAddTag = () => {
    const tagToAdd = newTag.trim();
    if (!tagToAdd || tags.includes(tagToAdd)) return;

    setTags([...tags, tagToAdd]);
    setNewTag('');
    setShowTagInput(false);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const linkUrls = parseLinkUrls(linkUrlsText);

    if (files.length === 0 && linkUrls.length === 0) {
      setError('Please select at least one file or provide at least one URL (one per line)');
      return;
    }

    for (const u of linkUrls) {
      const urlError = validateLinkUrl(u);
      if (urlError) {
        setError(`${urlError}: ${u}`);
        return;
      }
    }

    const oid = organisationId.trim();
    const oname = organisationName.trim();
    const hasOrgField = oid.length > 0 || oname.length > 0;
    if (hasOrgField) {
      if (!oid || !oname) {
        setError('Organisation ID and name must both be filled, or leave both empty.');
        return;
      }
      if (!projectId.trim()) {
        setError('Project ID is required when organisation details are provided.');
        return;
      }
    }

    setUploading(true);
    setError(null);

    try {
      const limit = pLimit(3);

      const totalProgress = new Array(files.length).fill(0);
      setFileProgress(() => [...totalProgress]);
      const uploadLinks = await Promise.all(
        files.map(async (file) => backend.generateUploadLink(file.name, file.type)),
      );
      const urls = await Promise.all(
        files.map(
          async (file, index): Promise<string> =>
            limit(async () => {
              const uploadLink = uploadLinks[index];
              await uploadFileInChunks(file, uploadLink.data.uploadUrl, undefined, (progress) => {
                totalProgress[index] = progress.progress;
                const percent = Math.round(totalProgress.reduce((a, b) => a + b, 0) / totalProgress.length);
                const speed = Math.round(progress.speedKbps);
                setUploadProgress(`${percent}% (${speed}kb/s)`);
                setFileProgress(() => [...totalProgress]);
              });
              return uploadLink.data.uri;
            }),
        ),
      );

      setUploadProgress(null);

      const uploadResult = await backend.uploadDocuments(
        {
          category,
          urls: [...urls, ...linkUrls],
          tags,
          projectId,
          webhookUrl,
          organisationId: oid || undefined,
          organisationName: oname || undefined,
        },
        () => {},
      );

      const sp = new URLSearchParams();
      sp.append('uploadId', uploadResult.data.uploadId);

      navigate('/uploads/' + uploadResult.data.uploadId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload documents');
    } finally {
      setUploading(false);
      setFileProgress(undefined);
      setUploadProgress(null);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer.files) {
      setFiles(Array.from(event.dataTransfer.files));
      setFileProgress(undefined);
    }
  };

  const hasExternalUrlInput = linkUrlsText.trim().length > 0;
  const linkUrlCount = parseLinkUrls(linkUrlsText).length;

  return (
    <div className="">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton />
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 dark:bg-indigo-900/10 rounded-lg">
                <ArrowUpTrayIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">Upload Documents</h1>
                <p className="text-sm text-gray-500 dark:text-zinc-400 mt-0.5">Add new documents to your collection</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-800 shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200 dark:border-zinc-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Upload New Documents</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
            Upload one or more documents to be processed and categorized. You can upload files directly or provide
            Dropbox shared links (one per line) or GCS URLs.
          </p>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <AuthGuard role="admin">
                  {/* Category Input */}
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-zinc-100">
                      Category (Optional)
                    </label>
                    <div className="mt-1 relative">
                      <Input
                        id="category"
                        list="category-list"
                        value={category}
                        disabled={platformData.categories.isLoading}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="Enter or select document category"
                        autoComplete="off"
                      />
                      <datalist id="category-list">
                        {(platformData.categories.data?.pageCategories ?? []).map((cat) => (
                          <option key={cat} value={cat} />
                        ))}
                      </datalist>
                      {platformData.categories.isLoading ? (
                        <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">Loading categories...</p>
                      ) : (platformData.categories.data?.pageCategories ?? []).length > 0 ? (
                        <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
                          {(platformData.categories.data?.pageCategories ?? []).length} existing{' '}
                          {(platformData.categories.data?.pageCategories ?? []).length === 1
                            ? 'category'
                            : 'categories'}{' '}
                          available
                        </p>
                      ) : (
                        <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">No existing categories found</p>
                      )}
                    </div>
                  </div>
                </AuthGuard>
                <AuthGuard roles={['user', 'admin', 'curator']}>
                  {/* Project ID Input */}
                  <div>
                    <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 dark:text-zinc-100">
                      Project ID (Optional)
                    </label>
                    <div className="mt-1">
                      <Input
                        id="projectId"
                        value={projectId}
                        onChange={(e) => setProjectId(e.target.value)}
                        placeholder="Enter project ID"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="organisationId"
                      className="block text-sm font-medium text-gray-700 dark:text-zinc-100"
                    >
                      Organisation ID (Optional)
                    </label>
                    <div className="mt-1">
                      <Input
                        id="organisationId"
                        value={organisationId}
                        onChange={(e) => setOrganisationId(e.target.value)}
                        placeholder="External organisation id"
                        autoComplete="off"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                      If set, Organisation name and Project ID are required; the organisation is created or updated and
                      linked to the project.
                    </p>
                  </div>
                  <div>
                    <label
                      htmlFor="organisationName"
                      className="block text-sm font-medium text-gray-700 dark:text-zinc-100"
                    >
                      Organisation name (Optional)
                    </label>
                    <div className="mt-1">
                      <Input
                        id="organisationName"
                        value={organisationName}
                        onChange={(e) => setOrganisationName(e.target.value)}
                        placeholder="Display name"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </AuthGuard>
                <AuthGuard roles={['admin']}>
                  {/* Webhook URL Input */}
                  <div>
                    <label htmlFor="webhookUrl" className="block text-sm font-medium text-gray-700 dark:text-zinc-100">
                      Webhook URL (Optional)
                    </label>
                    <div className="mt-1">
                      <Input
                        id="webhookUrl"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://webhook.site/..."
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </AuthGuard>

                {/* Tags Input */}
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-zinc-100">
                    Tags (Optional)
                  </label>
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <Tag key={tag} tag={tag} onRemove={() => handleRemoveTag(tag)} />
                      ))}
                      {!showTagInput ? (
                        <button
                          type="button"
                          onClick={() => setShowTagInput(true)}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:focus:ring-blue-300 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <PlusIcon className="h-3 w-3 mr-0.5" />
                          Add Tag
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Input
                            id="newTag"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            disabled={platformData.tags.isLoading}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTag();
                              } else if (e.key === 'Escape') {
                                setShowTagInput(false);
                                setNewTag('');
                              }
                            }}
                          />
                          <datalist id="available-tags">
                            {(platformData.tags.data?.tags ?? [])
                              .filter((tag) => !tags.includes(tag))
                              .map((tag) => (
                                <option key={tag} value={tag} />
                              ))}
                          </datalist>
                          <button
                            type="button"
                            onClick={handleAddTag}
                            disabled={!newTag.trim()}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium dark:text-blue-400 dark:hover:text-blue-300 dark:focus:ring-blue-300 text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowTagInput(false);
                              setNewTag('');
                            }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium dark:text-zinc-400 dark:hover:text-gray-300 dark:focus:ring-gray-300 text-gray-600 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Remote URLs */}
                <div>
                  <label htmlFor="link-urls" className="block text-sm font-medium text-gray-700 dark:text-zinc-100">
                    URLs (Optional)
                  </label>
                  <div className="mt-1">
                    <Textarea
                      id="link-urls"
                      value={linkUrlsText}
                      onChange={(e) => setLinkUrlsText(e.target.value)}
                      placeholder={'https://www.dropbox.com/s/...\nhttps://www.dropbox.com/s/...'}
                      rows={4}
                      autoComplete="off"
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
                    One URL per line — Dropbox shared links (<code className="text-xs">/s/</code>) or{' '}
                    <code className="text-xs">gs://</code> / <code className="text-xs">gcs://</code>
                  </p>
                </div>

                {/* File Upload Area */}
                <div
                  className={clsx(
                    'mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-zinc-700 border-dashed rounded-md transition-colors',
                    hasExternalUrlInput ? 'opacity-50' : 'hover:border-gray-400 dark:hover:border-zinc-600',
                  )}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className={clsx(
                          'relative cursor-pointer dark:bg-zinc-800 dark:text-zinc-100  dark:focus-within:text-zinc-100 dark:focus-within:bg-zinc-600 bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500',
                          hasExternalUrlInput && 'pointer-events-none',
                        )}
                      >
                        <span>Upload files</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          multiple
                          className="sr-only"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          disabled={hasExternalUrlInput}
                          accept=".pdf,.zip,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.html,.htm,.doc,.docx"
                        />
                      </label>

                      <span className="text-gray-400">or</span>

                      <label
                        htmlFor="folder-upload"
                        className={clsx(
                          'relative cursor-pointer dark:bg-zinc-800 dark:text-zinc-100  dark:focus-within:text-zinc-100 dark:focus-within:bg-zinc-600 bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500',
                          hasExternalUrlInput && 'pointer-events-none',
                        )}
                      >
                        <span>Upload folder</span>
                        <input
                          id="folder-upload"
                          name="folder-upload"
                          type="file"
                          multiple
                          // `webkitdirectory` is a non-standard browser attribute
                          // (Chrome/Edge/Safari) not in React's HTMLInputAttributes; spread
                          // as a string-keyed object so JSX accepts it.
                          {...({ webkitdirectory: 'true' } as Record<string, string>)}
                          className="sr-only"
                          onChange={handleFileChange}
                          disabled={hasExternalUrlInput}
                          accept=".pdf,.zip,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.html,.htm,.doc,.docx"
                        />
                      </label>

                      <span className="text-gray-400">or</span>

                      <span>drag and drop</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-zinc-400">
                      PDF, ZIP, Image (JPEG, PNG), Spreadsheet (XLSX, XLS, CSV), Word (DOCX, DOC), or HTML
                    </p>
                  </div>
                </div>

                {/* Selected Files */}
                {files.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-zinc-100 mb-2">Selected files</h3>
                    <ul className="mt-2 divide-y divide-gray-200 dark:divide-zinc-700 border border-gray-200 dark:border-zinc-700 rounded-md">
                      {files.map((file, index) => (
                        <li
                          key={index}
                          className="py-2 px-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-zinc-700"
                        >
                          <span className="text-sm text-gray-500 dark:text-zinc-400 truncate max-w-[80%]">
                            {file.name}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500 dark:text-zinc-400 truncate max-w-[80%]">
                              <FileSize bytes={file.size} />
                            </span>
                            {fileProgress && fileProgress[index] != null ? (
                              <span className="text-sm text-gray-500 dark:text-zinc-400 truncate max-w-[80%]">
                                {Math.round(fileProgress[index])}%
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setFiles(files.filter((_, i) => i !== index))}
                                className="text-sm text-red-600 dark:text-red-400 dark:hover:text-red-300 dark:focus:ring-red-300 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/10 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <Button onClick={() => navigate('/documents')} plain>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading || (files.length === 0 && linkUrlCount === 0)}
                color="indigo"
              >
                {uploading ? (uploadProgress ? `Uploading ${uploadProgress}...` : 'Uploading...') : 'Upload'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
